// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Dropdown } from '@loa/ui-components';
import type { DropdownOption } from '@loa/ui-components';
import { adminApi } from '../api/adminApi';
import type { AdvisorWithAdmin } from '../types/admin';

export type AdvisorFilterProps = Readonly<{
  /** Currently selected advisor ID or 'all' */
  value: string;
  /** Callback when selection changes */
  onChange: (advisorId: string) => void;
  /** Whether the filter is disabled */
  disabled?: boolean;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional custom className */
  className?: string;
}>;

/**
 * AdvisorFilter Component
 *
 * A searchable dropdown filter for selecting advisors.
 * Used in admin views to filter data by specific advisor.
 *
 * Features:
 * - Loads advisors list from API
 * - Searchable dropdown
 * - "All Advisors" option as default
 * - Caches advisor list to prevent redundant API calls
 */
export function AdvisorFilter({
  value,
  onChange,
  disabled = false,
  placeholder = 'Filter by Advisor',
  className
}: AdvisorFilterProps) {
  const [advisors, setAdvisors] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load advisors on mount
  useEffect(() => {
    let isMounted = true;

    const loadAdvisors = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await adminApi.listAdvisors({ status: 'active' }, 0, 100);

        if (!isMounted) return;

        const options: DropdownOption[] = [
          { text: 'All Advisors', value: 'all' },
          ...response.content.map((advisor: AdvisorWithAdmin) => ({
            text: `${advisor.firstName} ${advisor.lastName}`,
            value: String(advisor.id),
          })),
        ];

        setAdvisors(options);
      } catch (err) {
        if (!isMounted) return;

        console.error('Failed to load advisors:', err);
        setError('Failed to load advisors');
        // Still provide "All Advisors" option on error
        setAdvisors([{ text: 'All Advisors', value: 'all' }]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAdvisors();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Dropdown
      search
      placeholder={placeholder}
      options={advisors}
      value={value}
      onChange={(newValue) => onChange(newValue)}
      disabled={disabled || loading}
      error={error || undefined}
      className={className}
      aria-label="Filter by advisor"
      style={{ minWidth: '200px' }}
      data-testid="advisor-filter"
    />
  );
}

export default AdvisorFilter;
